import type {
    EmailTemplate,
    EmailTemplateCreateUpdate,
    QuoteTemplate,
} from '@common/types/quoteTemplates';
import axiosInstance from '@/services/axiosInstance';
import { formatTimeSlots } from '@/utils/datetime';

export interface PostIntakeEmailTemplatePayloadInterface {
    isPostIntakeEnable?: boolean;
    emailSubject?: string;
    intakeCompletionText?: string;
}

export async function updateEmailTemplate(
    quoteTemplateId: number,
    emailTemplateId: number,
    payload: EmailTemplateCreateUpdate,
) {
    return axiosInstance.put<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/email-templates/${emailTemplateId}`,
        payload,
    );
}

export async function createEmailTemplate(
    quoteTemplateId: number,
    payload: EmailTemplateCreateUpdate,
) {
    return axiosInstance.post<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/email-templates`,
        payload,
    );
}

export async function updatePostIntakeTemplate(
    quoteTemplateId: number,
    payload: PostIntakeEmailTemplatePayloadInterface,
) {
    return axiosInstance.put<QuoteTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/post-intake-submission-status`,
        payload,
    );
}

export async function getPostIntakeTemplateInfo(quoteTemplateId: number) {
    return axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/post-intake-email-template`,
    );
}

export async function sendQuoteFormLink(payload: {
    lawyerName: string;
    lawyerEmail: string;
    clientFirstName: string;
    clientEmail: string;
    ccEmails: string[];
    quoteFormLink: string;
    signature: string;
    lawFirmId: number;
}) {
    return axiosInstance.post<void>('/api/v1/emails/send-quote-form-link', payload);
}

export async function resendIntakeEmail(payload: { prospectId: string; contactId: string }) {
    return axiosInstance.post<string>('/api/v1/emails/resend-intake', payload);
}

export async function resendPostIntakeEmail(payload: { prospectId: string; contactId: string }) {
    return axiosInstance.post<string>('/api/v1/emails/resend-post-intake', payload);
}

export async function getQuoteEmailTemplate(quoteTemplateId: number | string) {
    return axiosInstance.get<EmailTemplate>(
        `api/v1/quote-templates/${quoteTemplateId}/quote-email-template`,
    );
}

export async function getIntakeEmail(quoteIntakeId: number | string) {
    const intakeEmailTemplateResp = await axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteIntakeId}/intake-email-template`,
    );
    const {
        emailTemplateId,
        isQuoteApprovalEnable,
        emailBodyClosing,
        emailBodyIntroduction,
        emailCC,
        emailSubject,
        expiry,
    } = intakeEmailTemplateResp.data;

    return {
        intakeEmailTemplateId: emailTemplateId,
        subject: emailSubject || '',
        bodyBeforeLink: emailBodyIntroduction || '',
        bodyAfterLink: emailBodyClosing || '',
        isQuoteApprovalOn: isQuoteApprovalEnable,
        contactCC: emailCC || [],
        expiry: expiry,
    };
}

export async function getPostIntakeEmail(quoteTemplateId: number) {
    const postIntakeResp = await getPostIntakeTemplateInfo(quoteTemplateId);
    const { emailSubject } = postIntakeResp.data;

    return {
        postIntakeSubject: emailSubject,
    };
}

export async function getIntakeReminderEmail(quoteTemplateId: number) {
    const intakeEmailReminderResp = await axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/intake-email-reminder-template`,
    );
    const {
        emailTemplateId,
        isQuoteReminderOn,
        emailBodyClosing,
        emailBodyIntroduction,
        emailCC,
        emailSubject,
        frequencyOfReminder,
        reminderTime,
        totalNumberOfReminders,
        enableReminderExhaustNotification,
    } = intakeEmailReminderResp.data;

    return {
        intakeReminderEmailTemplateId: emailTemplateId,
        reminderSubject: emailSubject || '',
        reminderBodyBeforeLink: emailBodyIntroduction || '',
        reminderBodyAfterLink: emailBodyClosing || '',
        reminderContactsCC: emailCC || [],
        isQuoteReminderOn: isQuoteReminderOn,
        emailFrequencyOfReminder: frequencyOfReminder || 'EveryThirdDay',
        reminderTime: reminderTime ? formatTimeSlots(reminderTime) : 'Morning(09:00 AM)',
        totalNumberOfReminders: totalNumberOfReminders || 3,
        enableReminderExhaustNotification: enableReminderExhaustNotification,
    };
}
